import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import SydButton from '../../../../components/commonDesign/Button'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    width: '100%'
  },
  buttonWrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    overflow: 'visible',
    pointerEvents: 'none',
    zIndex: 10000
  },
  buttonContainer: {
    position: 'absolute',
    pointerEvents: 'auto'
  },
  replyButton: {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'var(--color-primary-500)',
    color: 'var(--color-contrast-primary-500)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
    }
  }
}))

function SelectableMarkdown ({ content, onReply, customRenderers, className, contentRef }) {
  const classes = useStyles()
  const containerRef = useRef(null)
  const [selection, setSelection] = useState(null)
  const [buttonPosition, setButtonPosition] = useState(null)
  const selectionTimeoutRef = useRef(null)

  const handleSelection = useCallback(() => {
    // Clear any pending timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current)
      selectionTimeoutRef.current = null
    }

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      setSelection(null)
      setButtonPosition(null)
      return
    }

    const range = selection.getRangeAt(0)
    const selectedText = range.toString().trim()

    // Only show button if there's actual text selected and it's within our container
    if (!selectedText || selectedText.length === 0) {
      setSelection(null)
      setButtonPosition(null)
      return
    }

    // Check if selection is within our container
    // Handle both element and text nodes
    const container = containerRef.current
    if (!container) {
      setSelection(null)
      setButtonPosition(null)
      return
    }

    // Check if selection is within our container
    // Try multiple methods to handle different node types
    const ancestor = range.commonAncestorContainer
    let isWithinContainer = false

    if (ancestor.nodeType === Node.TEXT_NODE) {
      // For text nodes, check if parent is in container
      isWithinContainer = container.contains(ancestor.parentNode)
    } else if (ancestor.nodeType === Node.ELEMENT_NODE) {
      // For element nodes, check directly
      isWithinContainer = container.contains(ancestor)
    }

    // Also check if the range intersects with our container
    if (!isWithinContainer) {
      try {
        const containerRange = document.createRange()
        containerRange.selectNodeContents(container)
        isWithinContainer = containerRange.compareBoundaryPoints(Range.END_TO_START, range) < 0 &&
                            containerRange.compareBoundaryPoints(Range.START_TO_END, range) > 0
      } catch (e) {
        // Fallback: check if start and end containers are within
        isWithinContainer = container.contains(range.startContainer) && container.contains(range.endContainer)
      }
    }

    if (!isWithinContainer) {
      setSelection(null)
      setButtonPosition(null)
      return
    }

    // Calculate button position (above the selection, centered)
    const rect = range.getBoundingClientRect()
    const containerRect = containerRef.current.getBoundingClientRect()

    // Position button above selection, centered horizontally
    const buttonHeight = 32
    const offset = 8

    const position = {
      top: rect.top - containerRect.top - buttonHeight - offset,
      left: rect.left - containerRect.left + (rect.width / 2)
    }

    // Delay showing the button by 0.5 seconds
    selectionTimeoutRef.current = setTimeout(() => {
      // Re-check selection is still valid after delay
      const currentSelection = window.getSelection()
      if (currentSelection && currentSelection.rangeCount > 0) {
        const currentRange = currentSelection.getRangeAt(0)
        const currentText = currentRange.toString().trim()
        const ancestor = currentRange.commonAncestorContainer
        const isStillValid = containerRef.current && (
          container.contains(ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentNode : ancestor) ||
          (container.contains(currentRange.startContainer) && container.contains(currentRange.endContainer))
        )
        if (currentText === selectedText && isStillValid) {
          setSelection(selectedText)
          setButtonPosition(position)
        }
      }
      selectionTimeoutRef.current = null
    }, 500)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current)
      }
    }
  }, [])

  const handleReply = useCallback(() => {
    if (selection && onReply) {
      onReply(selection)
      // Clear selection
      window.getSelection().removeAllRanges()
      setSelection(null)
      setButtonPosition(null)
    }
  }, [selection, onReply])

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelection)
    document.addEventListener('mouseup', handleSelection)
    return () => {
      document.removeEventListener('selectionchange', handleSelection)
      document.removeEventListener('mouseup', handleSelection)
    }
  }, [handleSelection])

  // Hide button when clicking outside (but not when clicking the button itself)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't hide if clicking the button
      if (e.target.closest(`.${classes.replyButton}`)) {
        return
      }
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelection(null)
        setButtonPosition(null)
      }
    }

    if (selection) {
      // Use a small delay to avoid hiding immediately after selection
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)
      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [selection, classes.replyButton])

  const mergedRef = useCallback((node) => {
    containerRef.current = node
    if (contentRef) contentRef.current = node
  }, [contentRef])

  return (
    <>
      <div ref={mergedRef} className={`${classes.container} ${className || ''}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={customRenderers}
        >
          {content}
        </ReactMarkdown>
      </div>
      {/* Always render wrapper outside container to prevent layout shifts */}
      <div className={classes.buttonWrapper}>
        {selection && buttonPosition && (
          <div
            className={classes.buttonContainer}
            style={{
              top: `${Math.max(10, buttonPosition.top + (containerRef.current?.getBoundingClientRect().top || 0))}px`,
              left: `${Math.max(10, Math.min(buttonPosition.left + (containerRef.current?.getBoundingClientRect().left || 0), window.innerWidth - 100))}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <SydButton
              variant='primary'
              size='xs'
              className={classes.replyButton}
              onClick={handleReply}
            >
              <Icon name={ICON_NAMES.arrowForward} style={{ fontSize: '14px' }} />
              Add to Reply
            </SydButton>
          </div>
        )}
      </div>
    </>
  )
}

SelectableMarkdown.propTypes = {
  content: PropTypes.string.isRequired,
  onReply: PropTypes.func.isRequired,
  customRenderers: PropTypes.object,
  className: PropTypes.string,
  contentRef: PropTypes.object
}

export default SelectableMarkdown
